import { createContext, useContext } from "react";
import { useGamesQuery, useSportGamesQuery } from "./utils";

export interface Game {
  id: string;
  name: string;
  extGameId: string;
  providerName: string;
  extProviderId: string;
  providerDisplayName: string;
  isLive?: boolean;
  displayName?: string;
  image?: string | null;
  link?: string | null;
  category?: string;
}

export interface SportGame {
  id: string;
  sportType: string;
  sportDisplayName: string;
  tournament: string;
  tournamentDisplayName: string;
  image?: string | null;
  link?: string | null;
  isLive?: boolean;
}

export interface GameIndexes {
  byId: Record<string, Game>;
  byExtGameId: Record<string, Game>;
  byProvider: Record<string, Game[]>;
  allProviders: string[];
  filterGamesByExtIds: (extGameIds: string[], providerNames?: string[]) => Game[];
  getGameById: (id: string) => Game | undefined;
  getGameByExtId: (extGameId: string) => Game | undefined;
  getGamesByProvider: (providerName: string) => Game[];
}

export interface SportGameIndexes {
  bySportType: Record<string, SportGame[]>;
  byTournament: Record<string, SportGame[]>;
  allSportTypes: string[];
  filterSportGamesByConditions: (sportNames?: string[], tournamentNames?: string[]) => SportGame[];
}

export const GamesContext = createContext<string | undefined>(undefined);

const ALL_FILTER = "all";

const toFilterKey = (value: string | undefined) =>
  String(value || "")
    .trim()
    .toLowerCase();

const hasFilterMatch = (filterSet: Set<string>, values: Array<string | undefined>) => {
  for (const value of values) {
    if (filterSet.has(toFilterKey(value))) return true;
  }
  return false;
};

const isAllFilter = (values?: string[]) =>
  !values?.length || values.some((value) => toFilterKey(value) === ALL_FILTER);

const NO_GAMES: Game[] = [];
const NO_SPORT_GAMES: SportGame[] = [];

type GameCatalog = { liveGames: Game[]; indexes: GameIndexes };

const gameCatalogs = new WeakMap<Game[], GameCatalog>();
const sportGameCatalogs = new WeakMap<SportGame[], SportGameIndexes>();

const buildGameIndexes = (data: Game[], liveGames: Game[]): GameIndexes => {
  const byId: Record<string, Game> = {};
  const byExtGameId: Record<string, Game> = {};
  const byProvider: Record<string, Game[]> = {};
  const byProviderId: Record<string, Game[]> = {};
  const providerSet = new Set<string>();

  // index every game by id so historical references (e.g. an already-won game) resolve
  data.forEach((game: Game) => {
    byId[game.id] = game;
    byExtGameId[game.extGameId] = game;
  });

  // provider buckets only hold live games — these drive what is offered/displayed
  liveGames.forEach((game: Game) => {
    const providerName = game.providerName || game.extProviderId;
    if (providerName) {
      if (!byProvider[providerName]) {
        byProvider[providerName] = [];
      }
      byProvider[providerName].push(game);
      providerSet.add(providerName);
    }
    if (game.extProviderId) {
      if (!byProviderId[game.extProviderId]) {
        byProviderId[game.extProviderId] = [];
      }
      byProviderId[game.extProviderId].push(game);
    }
  });

  const allProviders = Array.from(providerSet);

  // Utility functions leveraging indexes — only return live games (offered/displayed)
  const filterGamesByExtIds = (extGameIds: string[], providerNames?: string[]): Game[] => {
    if (!extGameIds?.length) return [];

    if (extGameIds[0] === "all" && providerNames?.[0] === "all") {
      return liveGames;
    }

    if (extGameIds[0] === "all") {
      if (providerNames?.length) {
        // new promos pass provider ids, legacy promos pass names
        return providerNames.flatMap(
          (provider) => byProvider[provider] || byProviderId[provider] || []
        );
      }
      return liveGames;
    }

    return extGameIds
      .map((extId) => byExtGameId[extId])
      .filter((game): game is Game => Boolean(game) && game.isLive !== false);
  };

  const getGameById = (id: string): Game | undefined => byId[id];
  const getGameByExtId = (extGameId: string): Game | undefined => byExtGameId[extGameId];
  const getGamesByProvider = (providerName: string): Game[] => byProvider[providerName] || [];

  return {
    byId,
    byExtGameId,
    byProvider,
    allProviders,
    filterGamesByExtIds,
    getGameById,
    getGameByExtId,
    getGamesByProvider,
  };
};

const buildGameCatalog = (data: Game[]): GameCatalog => {
  // a game toggled IsLive=false in the backoffice must not be offered/displayed in the
  // widget or FS exchange; a missing flag is treated as live (older catalog data)
  const liveGames = data.filter((game: Game) => game.isLive !== false);
  return { liveGames, indexes: buildGameIndexes(data, liveGames) };
};

const buildSportGameIndexes = (sportGames: SportGame[]): SportGameIndexes => {
  const bySportType: Record<string, SportGame[]> = {};
  const byTournament: Record<string, SportGame[]> = {};
  const sportTypeSet = new Set<string>();

  sportGames.forEach((sportGame: SportGame) => {
    const sportTypeKey = toFilterKey(sportGame.sportType);
    const tournamentKey = toFilterKey(sportGame.tournament);

    if (sportTypeKey) {
      if (!bySportType[sportTypeKey]) {
        bySportType[sportTypeKey] = [];
      }
      bySportType[sportTypeKey].push(sportGame);
      sportTypeSet.add(sportGame.sportType);
    }

    if (tournamentKey) {
      if (!byTournament[tournamentKey]) {
        byTournament[tournamentKey] = [];
      }
      byTournament[tournamentKey].push(sportGame);
    }
  });

  const filterSportGamesByConditions = (
    sportNames?: string[],
    tournamentNames?: string[]
  ): SportGame[] => {
    const shouldShowAllSports = isAllFilter(sportNames);
    const shouldShowAllTournaments = isAllFilter(tournamentNames);
    const sportNameSet = new Set((sportNames || []).map(toFilterKey));
    const tournamentNameSet = new Set((tournamentNames || []).map(toFilterKey));

    return sportGames.filter((sportGame: SportGame) => {
      const sportMatches =
        shouldShowAllSports ||
        hasFilterMatch(sportNameSet, [sportGame.sportType, sportGame.sportDisplayName]);
      const tournamentMatches =
        shouldShowAllTournaments ||
        hasFilterMatch(tournamentNameSet, [sportGame.tournament, sportGame.tournamentDisplayName]);

      return sportMatches && tournamentMatches;
    });
  };

  return {
    bySportType,
    byTournament,
    allSportTypes: Array.from(sportTypeSet),
    filterSportGamesByConditions,
  };
};

const getGameCatalog = (data: Game[]) => {
  let catalog = gameCatalogs.get(data);
  if (!catalog) {
    catalog = buildGameCatalog(data);
    gameCatalogs.set(data, catalog);
  }
  return catalog;
};

const getSportGameIndexes = (sportGames: SportGame[]) => {
  let indexes = sportGameCatalogs.get(sportGames);
  if (!indexes) {
    indexes = buildSportGameIndexes(sportGames);
    sportGameCatalogs.set(sportGames, indexes);
  }
  return indexes;
};

const useGameCatalog = (enabled = true) => {
  const { data = NO_GAMES, isLoading } = useGamesQuery(useContext(GamesContext), enabled);
  return { ...getGameCatalog(data), isLoading };
};

export const useGames = (): Game[] => useGameCatalog().liveGames;

export const useGameIndexes = (enabled = true): GameIndexes => useGameCatalog(enabled).indexes;

export const useGamesLoading = (enabled = true): boolean => useGameCatalog(enabled).isLoading;

export const useSportGames = (enabled = true): SportGame[] => {
  const { data = NO_SPORT_GAMES } = useSportGamesQuery(useContext(GamesContext), enabled);
  return data;
};

export const useSportGameIndexes = (enabled = true): SportGameIndexes =>
  getSportGameIndexes(useSportGames(enabled));
